/**
 * Notification System Constants
 * ============================================================================
 *
 * Purpose:
 * --------
 * Centralized constants for the notification system.
 * Defines notification types, positions, durations, and configuration.
 *
 * This file provides:
 * - Notification types (success, error, warning, info, loading)
 * - Notification positions
 * - Duration presets
 * - Queue management settings
 * - Animation settings
 * - i18n key prefixes
 *
 * Design Principles:
 * ------------------
 * - Single source of truth for notification configuration
 * - Type-safe constants using 'as const'
 * - Organized by category
 * - Well-documented with usage examples
 *
 * Usage:
 * ------
 * ```typescript
 * import { NOTIFICATION_TYPE, NOTIFICATION_DURATION } from 'core/notifications';
 *
 * showNotification({
 *   type: NOTIFICATION_TYPE.SUCCESS,
 *   duration: NOTIFICATION_DURATION.MEDIUM,
 * });
 * ```
 * ============================================================================
 */

/**
 * Notification Types
 *
 * Defines the visual style and behavior of notifications.
 */
export const NOTIFICATION_TYPE = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  LOADING: 'loading',
} as const;

/**
 * Notification Positions
 *
 * Where notifications appear on screen.
 */
export const NOTIFICATION_POSITION = {
  TOP_LEFT: 'top-left',
  TOP_CENTER: 'top-center',
  TOP_RIGHT: 'top-right',
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM_CENTER: 'bottom-center',
  BOTTOM_RIGHT: 'bottom-right',
} as const;

/**
 * Notification Duration Presets (in milliseconds)
 *
 * Standard durations for different notification types.
 */
export const NOTIFICATION_DURATION = {
  SHORT: 2000,      // 2 seconds - Quick confirmations
  MEDIUM: 4000,     // 4 seconds - Standard notifications
  LONG: 6000,       // 6 seconds - Important messages
  PERSISTENT: 0,    // Never auto-dismiss (manual close only)
} as const;

/**
 * Default duration by notification type
 */
export const DEFAULT_DURATION_BY_TYPE = {
  [NOTIFICATION_TYPE.SUCCESS]: NOTIFICATION_DURATION.SHORT,
  [NOTIFICATION_TYPE.ERROR]: NOTIFICATION_DURATION.LONG,
  [NOTIFICATION_TYPE.WARNING]: NOTIFICATION_DURATION.MEDIUM,
  [NOTIFICATION_TYPE.INFO]: NOTIFICATION_DURATION.MEDIUM,
  [NOTIFICATION_TYPE.LOADING]: NOTIFICATION_DURATION.PERSISTENT,
} as const;

/**
 * Notification Priority
 *
 * Higher priority notifications are shown first in queue.
 */
export const NOTIFICATION_PRIORITY = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  CRITICAL: 4,
} as const;

/**
 * Default priority by notification type
 */
export const DEFAULT_PRIORITY_BY_TYPE = {
  [NOTIFICATION_TYPE.SUCCESS]: NOTIFICATION_PRIORITY.LOW,
  [NOTIFICATION_TYPE.ERROR]: NOTIFICATION_PRIORITY.HIGH,
  [NOTIFICATION_TYPE.WARNING]: NOTIFICATION_PRIORITY.MEDIUM,
  [NOTIFICATION_TYPE.INFO]: NOTIFICATION_PRIORITY.LOW,
  [NOTIFICATION_TYPE.LOADING]: NOTIFICATION_PRIORITY.MEDIUM,
} as const;

/**
 * Notification Configuration
 *
 * Global settings for notification behavior.
 */
export const NOTIFICATION_CONFIG = {
  // Maximum notifications visible at once
  MAX_VISIBLE: 3,

  // Maximum notifications in queue
  MAX_QUEUE_SIZE: 10,

  // Default position
  DEFAULT_POSITION: NOTIFICATION_POSITION.TOP_RIGHT,

  // Animation duration (milliseconds)
  ANIMATION_DURATION: 300,

  // Gap between notifications (pixels)
  GAP: 16,

  // Enable/disable notification sounds
  ENABLE_SOUNDS: false,

  // Enable/disable notification vibration (mobile)
  ENABLE_VIBRATION: false,

  // Prevent duplicate notifications within this time (milliseconds)
  DUPLICATE_PREVENTION_WINDOW: 2000,

  // Auto-dismiss on user interaction (click/tap)
  DISMISS_ON_CLICK: true,

  // Show close button
  SHOW_CLOSE_BUTTON: true,

  // Show progress bar
  SHOW_PROGRESS_BAR: true,

  // Pause on hover
  PAUSE_ON_HOVER: true,

  // Maximum notification history to store
  MAX_HISTORY_SIZE: 50,
} as const;

/**
 * Notification Action Types
 *
 * Types of actions that can be attached to notifications.
 */
export const NOTIFICATION_ACTION = {
  BUTTON: 'button',
  LINK: 'link',
  DISMISS: 'dismiss',
  CUSTOM: 'custom',
} as const;

/**
 * Confirmation Dialog Types
 */
export const CONFIRMATION_TYPE = {
  INFO: 'info',
  WARNING: 'warning',
  DANGER: 'danger',
  SUCCESS: 'success',
} as const;

/**
 * Confirmation Dialog Sizes
 */
export const CONFIRMATION_SIZE = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
} as const;

/**
 * Storage Keys
 *
 * LocalStorage keys for notification persistence.
 */
export const NOTIFICATION_STORAGE_KEYS = {
  HISTORY: 'bimanova_notification_history',
  PREFERENCES: 'bimanova_notification_preferences',
} as const;

/**
 * i18n Keys
 *
 * Translation key prefixes for notifications.
 */
export const NOTIFICATION_I18N_KEYS = {
  NOTIFICATIONS: 'notifications',
  SUCCESS: 'notifications:success',
  ERROR: 'notifications:error',
  WARNING: 'notifications:warning',
  INFO: 'notifications:info',
  ACTIONS: 'notifications:actions',
  CONFIRMATION: 'notifications:confirmation',
} as const;

/**
 * Notification Icons
 *
 * Icon mappings for notification types (using Heroicons or custom).
 */
export const NOTIFICATION_ICONS = {
  [NOTIFICATION_TYPE.SUCCESS]: 'CheckCircleIcon',
  [NOTIFICATION_TYPE.ERROR]: 'XCircleIcon',
  [NOTIFICATION_TYPE.WARNING]: 'ExclamationTriangleIcon',
  [NOTIFICATION_TYPE.INFO]: 'InformationCircleIcon',
  [NOTIFICATION_TYPE.LOADING]: 'ArrowPathIcon',
} as const;

/**
 * Notification Colors (Tailwind classes)
 *
 * Color schemes for different notification types.
 */
export const NOTIFICATION_COLORS = {
  [NOTIFICATION_TYPE.SUCCESS]: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'text-green-600',
    text: 'text-green-900',
  },
  [NOTIFICATION_TYPE.ERROR]: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-600',
    text: 'text-red-900',
  },
  [NOTIFICATION_TYPE.WARNING]: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icon: 'text-yellow-600',
    text: 'text-yellow-900',
  },
  [NOTIFICATION_TYPE.INFO]: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-600',
    text: 'text-blue-900',
  },
  [NOTIFICATION_TYPE.LOADING]: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    icon: 'text-gray-600',
    text: 'text-gray-900',
  },
} as const;

/**
 * Error Codes
 *
 * Notification system error codes.
 */
export const NOTIFICATION_ERROR_CODES = {
  QUEUE_FULL: 'NOTIFICATION_QUEUE_FULL',
  INVALID_TYPE: 'NOTIFICATION_INVALID_TYPE',
  INVALID_DURATION: 'NOTIFICATION_INVALID_DURATION',
  NOT_FOUND: 'NOTIFICATION_NOT_FOUND',
} as const;
